import { apiFetch } from '@/lib/api/client';

/**
 * SAS-upload helper. Two-step:
 *   1) POST /uploads/sas → get a write-only SAS URL.
 *   2) PUT the file bytes directly to Azure Storage at that URL with
 *      x-ms-blob-type: BlockBlob. Returns the canonical blob URL (without SAS)
 *      that callers persist on their domain rows.
 *
 * Note: the read URL needs its own SAS (or a proxy through the API) to be
 * fetched later. v1 stores the bare blob URL and gates reads through APIM.
 */

export type UploadPurpose = 'task-proof' | 'incident-photo';

interface SasResponse {
  uploadUrl: string;
  blobUrl: string;
  container: string;
  blobName: string;
  expiresAt: string;
}

export async function uploadFile(
  file: File,
  purpose: UploadPurpose,
  storeId: string,
): Promise<string> {
  const sas = await apiFetch<SasResponse>('/uploads/sas', {
    method: 'POST',
    body: JSON.stringify({
      purpose,
      storeId,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
    }),
  });
  const res = await fetch(sas.uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'content-type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`upload failed: ${res.status} ${res.statusText}`);
  }
  return sas.blobUrl;
}
